/**
 * Notification Settings — org-wide Slack channel notification config.
 *
 * Extracted verbatim from the former Notifications page so it can live inside
 * the new /notifications "Settings" tab. Logic is unchanged; only the export
 * name changed (default `NotificationsPage` -> named `NotificationSettings`).
 *
 * Moved out of Stock Sync (where this card originally lived) so that the
 * PO daily digests and the per-tenant stock-sync events share one home.
 * Reads/writes the same JSON path on `organizations.settings.inventoryPush.notifications`
 * so existing webhook URLs and event toggles roll forward unchanged.
 *
 * Slack incoming webhooks are bound to ONE channel at creation time on
 * Slack's side. To send digests to a different channel, the operator
 * creates a new incoming webhook in Slack pointed at that channel and
 * pastes its URL here.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SplitOrderNotificationCard } from './SplitOrderNotificationCard';
import { toast } from 'sonner';
import {
  Activity,
  AlarmClock,
  AlertCircle,
  Beaker,
  Bell,
  Boxes,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Factory,
  FlaskConical,
  GitBranch,
  Handshake,
  Loader2,
  MessageSquare,
  PackageX,
  Paperclip,
  Save,
  Send,
  ShieldAlert,
  Ship,
  TrendingDown,
  TrendingUp,
  UserPlus,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrganizationMutations } from '@/features/organization/hooks/useOrganizationMutations';
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  resolveInventoryPushSettings,
  resolveNotificationSettings,
  type NotificationSettings,
} from '@/features/organization/components/settingsShared';
import type { OrganizationSettings as SharedOrganizationSettings } from '@threadheads/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SlackChannelPicker } from '@/features/organization/components/SlackChannelPicker';
import { PartnerDigestsCard } from '@/features/partner-digests/components/PartnerDigestsCard';

export function NotificationSettings() {
  const { organization } = useOrganization();
  const { updateOrganization } = useOrganizationMutations();

  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(() =>
    resolveNotificationSettings(organization?.settings as SharedOrganizationSettings | null),
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const orgSettings = organization?.settings as SharedOrganizationSettings | null;
    setNotifSettings(resolveNotificationSettings(orgSettings));
    setHasChanges(false);
  }, [organization]);

  const updateNotif = (partial: Partial<NotificationSettings>) => {
    setNotifSettings((prev) => ({ ...prev, ...partial }));
    setHasChanges(true);
  };

  const handleReset = () => {
    const orgSettings = organization?.settings as SharedOrganizationSettings | null;
    setNotifSettings(resolveNotificationSettings(orgSettings));
    setHasChanges(false);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const orgSettings = (organization?.settings ?? {}) as unknown as SharedOrganizationSettings;
    const push = resolveInventoryPushSettings(orgSettings);
    updateOrganization.mutate({
      settings: {
        ...orgSettings,
        inventoryPush: {
          ...push,
          notifications: notifSettings,
        },
      },
    });
  };

  const testSlack = trpc.stockSync.testSlackNotification.useMutation({
    onSuccess: () => toast.success('Test message sent to Slack'),
    onError: (err) => toast.error(err.message),
  });

  const testOosChannel = trpc.slackThreadbot.testChannel.useMutation({
    onSuccess: () => toast.success('Test message sent to the channel'),
    onError: (err) => toast.error(err.message),
  });

  // Per-org Slack connection status. getStatus is scoped to the caller's org
  // on the backend, so this reflects THIS organization's Threadbot — every
  // tenant connects its own Slack app.
  const threadbotStatus = trpc.slackThreadbot.getStatus.useQuery();
  const slackConnected = Boolean(
    threadbotStatus.data?.hasBotToken && threadbotStatus.data?.appId,
  );

  const membersQuery = trpc.organization.getMembers.useQuery(undefined, {
    enabled: notifSettings.notifyOnOosDigest,
  });
  const activeMembers = useMemo(
    () => (membersQuery.data ?? [])
      .filter((member) => member.isActive && member.email)
      .map((member) => ({
        id: member.id,
        label: [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email,
        email: member.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [membersQuery.data],
  );

  const toggleOosRecipient = (profileId: string) => {
    const selected = notifSettings.oosDigestRecipientIds;
    if (!selected.includes(profileId) && selected.length >= 25) {
      toast.error('You can notify up to 25 teammates');
      return;
    }
    updateNotif({
      oosDigestRecipientIds: selected.includes(profileId)
        ? selected.filter((id) => id !== profileId)
        : [...selected, profileId],
    });
  };

  const testPipeline = trpc.pipelineDigest.testSend.useMutation({
    onSuccess: (r) =>
      toast.success(`Pipeline digest sent: ${r.total} POs in flight, ${r.inDev} in dev`),
    onError: (err) => toast.error(err.message),
  });

  const testPushDigest = trpc.pushDigest.testSend.useMutation({
    onSuccess: (r) =>
      toast.success(`Push digest sent: ${r.totalPushes} pushes in the last 24 h`),
    onError: (err) => toast.error(err.message),
  });

  const testProductionDaily = trpc.productionDigest.testSendDaily.useMutation({
    onSuccess: (r) =>
      toast.success(
        `Daily production digest sent: ${r.unitsPrinted} printed, ${r.unitsPacked} packed yesterday`,
      ),
    onError: (err) => toast.error(err.message),
  });

  const testProductionWeekly = trpc.productionDigest.testSendWeekly.useMutation({
    onSuccess: (r) =>
      toast.success(
        `Weekly production roll-up sent: ${r.unitsPrinted} printed, ${r.unitsPacked} packed last week`,
      ),
    onError: (err) => toast.error(err.message),
  });

  const testIncomingShipments = trpc.incomingShipments.testSend.useMutation({
    onSuccess: (r) =>
      toast.success(
        `Incoming shipments digest sent: ${r.thisWeek} arriving this week, ${r.overdue} overdue`,
      ),
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-threadheads-purple/10">
            <Bell className="h-5 w-5 text-threadheads-purple" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
            <p className="text-sm text-muted-foreground">
              Send operational digests and stock-sync alerts to Slack.
            </p>
          </div>
        </div>
      </header>

      {/* Per-org connect prompt: the channels/toggles below only post once THIS
          organization has a Slack app connected. Shown only when unconnected. */}
      {threadbotStatus.isSuccess && !slackConnected && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-amber-900">
              Slack isn't connected for this organization yet.
            </p>
            <p className="mt-0.5 text-amber-800">
              The channels and toggles below won't post anywhere until you connect a Slack app for
              this org. Connect it in{' '}
              <Link
                to="/settings?tab=integrations"
                className="font-medium underline underline-offset-2 hover:text-amber-900"
              >
                Settings → Integrations → Slack DMs
              </Link>
              .
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Slack Notifications */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100">
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-violet-600" fill="currentColor" aria-hidden="true">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.52 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.167 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.124a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.167 24a2.527 2.527 0 0 1-2.52-2.52v-2.523h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.52 2.523h-6.313z" />
                </svg>
              </div>
              <div className="flex-1">
                <CardTitle className="text-base">Slack Channel Notifications</CardTitle>
                <CardDescription>
                  Channel-wide digests via an incoming webhook. For per-user @-mention DMs,
                  see Settings → Integrations → Slack DMs (Threadbot).
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable Slack notifications"
                checked={notifSettings.slackEnabled}
                onCheckedChange={(checked) => updateNotif({ slackEnabled: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.slackEnabled && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label htmlFor="slack-webhook-url">Webhook URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="slack-webhook-url"
                    type="url"
                    placeholder="https://hooks.slack.com/services/T.../B.../..."
                    value={notifSettings.slackWebhookUrl ?? ''}
                    onChange={(e) => updateNotif({ slackWebhookUrl: e.target.value || null })}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.slackWebhookUrl) {
                        toast.error('Enter a webhook URL first');
                        return;
                      }
                      testSlack.mutate({ webhookUrl: notifSettings.slackWebhookUrl });
                    }}
                    disabled={!notifSettings.slackWebhookUrl || testSlack.isPending}
                    className="shrink-0"
                  >
                    {testSlack.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Create an incoming webhook in Slack (Apps → Incoming Webhooks → Add to Slack)
                  and pick the channel during setup. The webhook URL is bound to that one
                  channel. To post to a different channel, create a new webhook there and paste
                  its URL here.
                </p>
              </div>

              {/* Purchase Order digests */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">Purchase Order Digests</p>
                  <Badge variant="outline" className="text-[10px]">Daily · 08:00 AEST</Badge>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <TrendingDown className="h-4 w-4 text-amber-600 shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-po-behind">POs behind pace</Label>
                      {/* PR #1989 review: in transit is measured against the arrival ETA (Ready ETA when none is set). */}
                      <p className="text-sm text-muted-foreground">
                        Submitted → production over 10 days, in production over 45 days,
                        ready over 14 days, or past arrival ETA while in transit.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-po-behind"
                    checked={notifSettings.notifyOnPoBehindPace}
                    onCheckedChange={(checked) => updateNotif({ notifyOnPoBehindPace: checked })}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="h-4 w-4 text-blue-600 shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-po-updates">PO status updates</Label>
                      <p className="text-sm text-muted-foreground">
                        Every status transition that fired in the last 24 hours.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-po-updates"
                    checked={notifSettings.notifyOnPoStatusUpdates}
                    onCheckedChange={(checked) => updateNotif({ notifyOnPoStatusUpdates: checked })}
                  />
                </div>
              </div>

              {/* Stock Sync events */}
              <div className="space-y-4 pt-2 border-t">
                <p className="text-sm font-medium">Stock Sync Events</p>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <ShieldAlert className="h-4 w-4 text-red-500 shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-halt">Mass Out-of-Stock Halt</Label>
                      <p className="text-sm text-muted-foreground">
                        When the push is halted because too many SKUs would go to zero.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-halt"
                    checked={notifSettings.notifyOnHalt}
                    onCheckedChange={(checked) => updateNotif({ notifyOnHalt: checked })}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <ClipboardCheck className="h-4 w-4 text-amber-500 shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-approval">Pending Approval</Label>
                      <p className="text-sm text-muted-foreground">
                        When significant stock drops are held for admin review.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-approval"
                    checked={notifSettings.notifyOnPendingApproval}
                    onCheckedChange={(checked) => updateNotif({ notifyOnPendingApproval: checked })}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-error">Push Errors</Label>
                      <p className="text-sm text-muted-foreground">
                        When Shopify API calls fail during a push cycle.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-error"
                    checked={notifSettings.notifyOnPushError}
                    onCheckedChange={(checked) => updateNotif({ notifyOnPushError: checked })}
                  />
                </div>

              </div>
            </CardContent>
          )}
        </Card>

        {/* Daily push digest — posts via Threadbot to a chosen channel.
            Replaced the per-cycle "Push Summary" webhook toggle (the
            notifyOnPushSummary key is kept in the schema so legacy saved
            settings don't get stripped; the UI row is gone). */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100">
                <Activity className="h-5 w-5 text-sky-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Push Daily Digest</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Daily · 08:00 AEST</Badge>
                </div>
                <CardDescription>
                  One roll-up a day of what the inventory push did: totals, per-location
                  breakdown, and any errors. Posts via Threadbot, so you can pick the channel.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable push daily digest"
                checked={notifSettings.notifyOnPushDigest}
                onCheckedChange={(checked) => updateNotif({ notifyOnPushDigest: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnPushDigest && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <SlackChannelPicker
                      value={notifSettings.pushDigestChannel}
                      onChange={(next) => updateNotif({ pushDigestChannel: next })}
                      enabled={notifSettings.notifyOnPushDigest}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.pushDigestChannel) {
                        toast.error('Pick a channel first');
                        return;
                      }
                      testPushDigest.mutate({ channel: notifSettings.pushDigestChannel });
                    }}
                    disabled={!notifSettings.pushDigestChannel || testPushDigest.isPending}
                    className="shrink-0"
                  >
                    {testPushDigest.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs). Invite
                  Threadbot to the channel, then hit refresh. Test posts the real digest now.
                </p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Production digest — daily previous-day stats + Monday weekly roll-up
            with week-over-week trends. Two toggles share one channel. */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100">
                <Factory className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Production Digest</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Daily + weekly · 08:00 AEST</Badge>
                </div>
                <CardDescription>
                  What production did: orders completed, units printed and packed, redos.
                  Posts via Threadbot, so you can pick the channel.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 border-t pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <CalendarDays className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div className="space-y-0.5">
                    <Label htmlFor="notify-production-daily">Daily stats</Label>
                    <p className="text-sm text-muted-foreground">
                      Each morning, the previous day's numbers with a per-location
                      breakdown of printed units.
                    </p>
                  </div>
                </div>
                <Switch
                  id="notify-production-daily"
                  checked={notifSettings.notifyOnProductionDaily}
                  onCheckedChange={(checked) => updateNotif({ notifyOnProductionDaily: checked })}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div className="space-y-0.5">
                    <Label htmlFor="notify-production-weekly">Weekly roll-up with trends</Label>
                    <p className="text-sm text-muted-foreground">
                      Monday mornings, last week's totals with week-over-week deltas.
                    </p>
                  </div>
                </div>
                <Switch
                  id="notify-production-weekly"
                  checked={notifSettings.notifyOnProductionWeekly}
                  onCheckedChange={(checked) => updateNotif({ notifyOnProductionWeekly: checked })}
                />
              </div>
            </div>

            {(notifSettings.notifyOnProductionDaily || notifSettings.notifyOnProductionWeekly) && (
              <div className="space-y-2 pt-2 border-t">
                <Label>Channel</Label>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <SlackChannelPicker
                      value={notifSettings.productionDigestChannel}
                      onChange={(next) => updateNotif({ productionDigestChannel: next })}
                      enabled={
                        notifSettings.notifyOnProductionDaily ||
                        notifSettings.notifyOnProductionWeekly
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.productionDigestChannel) {
                        toast.error('Pick a channel first');
                        return;
                      }
                      testProductionDaily.mutate({ channel: notifSettings.productionDigestChannel });
                    }}
                    disabled={!notifSettings.productionDigestChannel || testProductionDaily.isPending}
                    className="shrink-0"
                  >
                    {testProductionDaily.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test daily
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.productionDigestChannel) {
                        toast.error('Pick a channel first');
                        return;
                      }
                      testProductionWeekly.mutate({ channel: notifSettings.productionDigestChannel });
                    }}
                    disabled={!notifSettings.productionDigestChannel || testProductionWeekly.isPending}
                    className="shrink-0"
                  >
                    {testProductionWeekly.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test weekly
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Both digests share this channel. Uses the Threadbot bot token (Settings →
                  Integrations → Slack DMs). Invite Threadbot to the channel, then hit refresh.
                  Test posts the real digest now.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Incoming shipments — Monday roll-up of in-transit POs by destination */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-100">
                <Ship className="h-5 w-5 text-cyan-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Incoming Shipments</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Weekly · Mon 08:00 AEST</Badge>
                </div>
                <CardDescription>
                  What's landing this week, where: in-transit POs grouped by destination,
                  with anything overdue flagged. Posts via Threadbot, so you can pick the channel.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable incoming shipments digest"
                checked={notifSettings.notifyOnIncomingShipments}
                onCheckedChange={(checked) => updateNotif({ notifyOnIncomingShipments: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnIncomingShipments && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <SlackChannelPicker
                      value={notifSettings.incomingShipmentsChannel}
                      onChange={(next) => updateNotif({ incomingShipmentsChannel: next })}
                      enabled={notifSettings.notifyOnIncomingShipments}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.incomingShipmentsChannel) {
                        toast.error('Pick a channel first');
                        return;
                      }
                      testIncomingShipments.mutate({ channel: notifSettings.incomingShipmentsChannel });
                    }}
                    disabled={!notifSettings.incomingShipmentsChannel || testIncomingShipments.isPending}
                    className="shrink-0"
                  >
                    {testIncomingShipments.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs). Invite
                  Threadbot to the channel, then hit refresh. Test posts the real digest now.
                </p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Weekly starred-item stock digest — posts via Threadbot to a chosen channel */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-100">
                <PackageX className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Weekly Key-Item Stock Digest</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Weekly · Tue 09:00 AEST</Badge>
                </div>
                <CardDescription>
                  A weekly bird's-eye view of starred SKUs in PrintRun's red and yellow
                  stock bands. Posts via Threadbot, so every organization picks its own channel.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable weekly key-item stock digest"
                checked={notifSettings.notifyOnOosDigest}
                onCheckedChange={(checked) => updateNotif({ notifyOnOosDigest: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnOosDigest && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <SlackChannelPicker
                      value={notifSettings.oosDigestChannel}
                      onChange={(next) => updateNotif({ oosDigestChannel: next })}
                      enabled={notifSettings.notifyOnOosDigest}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.oosDigestChannel) {
                        toast.error('Pick a channel first');
                        return;
                      }
                      testOosChannel.mutate({ channel: notifSettings.oosDigestChannel });
                    }}
                    disabled={!notifSettings.oosDigestChannel || testOosChannel.isPending}
                    className="shrink-0"
                  >
                    {testOosChannel.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs).
                  Invite Threadbot to the channel, then hit refresh. Private channels
                  won't list until the bot is a member.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Notify teammates</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-start sm:w-96">
                      <UserPlus className="mr-2 h-4 w-4" />
                      {notifSettings.oosDigestRecipientIds.length > 0
                        ? `${notifSettings.oosDigestRecipientIds.length} teammate${notifSettings.oosDigestRecipientIds.length === 1 ? '' : 's'} selected`
                        : 'Select teammates'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 p-2">
                    <p className="px-2 pb-2 text-xs text-muted-foreground">
                      Selected teammates are @mentioned in the channel when their PrintRun email matches Slack.
                    </p>
                    <div className="max-h-64 space-y-0.5 overflow-y-auto">
                      {membersQuery.isLoading && (
                        <p className="px-2 py-3 text-sm text-muted-foreground">Loading teammates…</p>
                      )}
                      {!membersQuery.isLoading && activeMembers.length === 0 && (
                        <p className="px-2 py-3 text-sm text-muted-foreground">No active teammates found.</p>
                      )}
                      {activeMembers.map((member) => (
                        <label
                          key={member.id}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted"
                        >
                          <Checkbox
                            checked={notifSettings.oosDigestRecipientIds.includes(member.id)}
                            onCheckedChange={() => toggleOosRecipient(member.id)}
                          />
                          <span className="min-w-0 flex-1 truncate">{member.label}</span>
                          <span className="max-w-36 truncate text-xs text-muted-foreground">
                            {member.email}
                          </span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  The channel always receives the digest. Teammates are optional and configured separately for each organization.
                </p>
              </div>

              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <p className="font-medium">What gets reported</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Starred SKUs only. Red means at or below the restock threshold; yellow means above the threshold but no more than twice it. Star items and set thresholds in Inventory.
                </p>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="oos-exclude-preprinted">Exclude pre-printed stock</Label>
                  <p className="text-xs text-muted-foreground">
                    Skip pre-printed SKUs: POD items printed ahead of demand and held as PAP
                    stock. They get reprinted on demand, so a low count usually isn't worth an alert.
                  </p>
                </div>
                <Switch
                  id="oos-exclude-preprinted"
                  aria-label="Exclude pre-printed stock from the weekly key-item digest"
                  checked={notifSettings.oosDigestExcludePreprinted}
                  onCheckedChange={(checked) =>
                    updateNotif({ oosDigestExcludePreprinted: checked })
                  }
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Weekly pipeline digest — Bulk/Blanks PO counts + Product Dev in dev */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                <Boxes className="h-5 w-5 text-slate-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Weekly Pipeline Digest</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Weekly · Mon 08:00 AEST</Badge>
                </div>
                <CardDescription>
                  A Monday roll-up: Bulk / Blanks POs counted by phase (in production / packing,
                  in transit, delivered) plus new products still in development. Posts via Threadbot.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable weekly pipeline digest"
                checked={notifSettings.notifyOnPipelineDigest}
                onCheckedChange={(checked) => updateNotif({ notifyOnPipelineDigest: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnPipelineDigest && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <SlackChannelPicker
                      value={notifSettings.pipelineDigestChannel}
                      onChange={(next) => updateNotif({ pipelineDigestChannel: next })}
                      enabled={notifSettings.notifyOnPipelineDigest}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!notifSettings.pipelineDigestChannel) {
                        toast.error('Pick a channel first');
                        return;
                      }
                      testPipeline.mutate({ channel: notifSettings.pipelineDigestChannel });
                    }}
                    disabled={!notifSettings.pipelineDigestChannel || testPipeline.isPending}
                    className="shrink-0"
                  >
                    {testPipeline.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs). Invite
                  Threadbot to the channel, then hit refresh. Test posts the real digest now.
                </p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Weekly hustle digest — the nag: overdue Product Dev styles, stale
            styles (no movement in N days), and overdue POs. Cron-only (no
            test-send endpoint), so this card has no Test button. */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100">
                <AlarmClock className="h-5 w-5 text-rose-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Weekly Hustle Digest</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Weekly · Mon 08:00 AEST</Badge>
                </div>
                <CardDescription>
                  A Monday nag: overdue Product Dev styles, styles with no movement in a while,
                  and overdue purchase orders, each deep-linked so the chase happens in the
                  channel. Posts via Threadbot, so you can pick the channel.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable weekly hustle digest"
                checked={notifSettings.notifyOnHustleDigest}
                onCheckedChange={(checked) => updateNotif({ notifyOnHustleDigest: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnHustleDigest && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <SlackChannelPicker
                  value={notifSettings.hustleDigestChannel}
                  onChange={(next) => updateNotif({ hustleDigestChannel: next })}
                  enabled={notifSettings.notifyOnHustleDigest}
                />
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs). Invite
                  Threadbot to the channel, then hit refresh. Silently skips when no channel
                  is configured.
                </p>
              </div>

              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="hustle-stale-days">Flag styles as stale after</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="hustle-stale-days"
                    type="number"
                    min={1}
                    max={90}
                    value={notifSettings.hustleDigestStaleDays}
                    onChange={(e) => {
                      const v = Number.parseInt(e.target.value, 10);
                      updateNotif({
                        hustleDigestStaleDays: Number.isNaN(v)
                          ? DEFAULT_NOTIFICATION_SETTINGS.hustleDigestStaleDays
                          : Math.min(90, Math.max(1, v)),
                      });
                    }}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">days with no movement</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  A Product Dev style with no update in this many days is flagged as stale in the
                  digest. Between 1 and 90 days.
                </p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Partner portal — supplier-org side: ping a channel the moment a
            partner buyer submits a PO to us. Posts via OUR org's Threadbot
            (per-org Slack identity), fired by the buyer's submit transition. */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-threadheads-purple/10">
                <Handshake className="h-5 w-5 text-threadheads-purple" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Partner PO Submitted</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Instant</Badge>
                </div>
                <CardDescription>
                  When a partner buyer submits a purchase order to your organisation,
                  post it to a channel the moment it lands. Posts via Threadbot, so you
                  can pick the channel.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable partner PO submitted notifications"
                checked={notifSettings.notifyOnPartnerPoSubmit}
                onCheckedChange={(checked) => updateNotif({ notifyOnPartnerPoSubmit: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnPartnerPoSubmit && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <SlackChannelPicker
                  value={notifSettings.partnerPoSubmitChannel}
                  onChange={(next) => updateNotif({ partnerPoSubmitChannel: next })}
                  enabled={notifSettings.notifyOnPartnerPoSubmit}
                />
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs). Invite
                  Threadbot to the channel, then hit refresh. Silently skips when no channel
                  is configured.
                </p>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Product Dev collaboration — ping a channel whenever a partner-worked
            Product Dev record changes (samples, stages, comments, files). Posts
            via OUR org's Threadbot; the channel can be one shared with the factory. */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-threadheads-purple/10">
                <FlaskConical className="h-5 w-5 text-threadheads-purple" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Product Dev updates</CardTitle>
                  <Badge variant="outline" className="text-[10px]">Instant</Badge>
                </div>
                <CardDescription>
                  Post to a Slack channel when a partner-worked Product Dev record changes
                  (samples, stages, comments, files). Uses this org's Threadbot; the channel
                  can be one you share with your factory.
                </CardDescription>
              </div>
              <Switch
                aria-label="Enable Product Dev update notifications"
                checked={notifSettings.notifyOnProductDevUpdates}
                onCheckedChange={(checked) => updateNotif({ notifyOnProductDevUpdates: checked })}
              />
            </div>
          </CardHeader>

          {notifSettings.notifyOnProductDevUpdates && (
            <CardContent className="space-y-6 border-t pt-6">
              <div className="space-y-2">
                <Label>Channel</Label>
                <SlackChannelPicker
                  value={notifSettings.productDevUpdatesChannel}
                  onChange={(next) => updateNotif({ productDevUpdatesChannel: next })}
                  enabled={notifSettings.notifyOnProductDevUpdates}
                />
                <p className="text-xs text-muted-foreground">
                  Uses the Threadbot bot token (Settings → Integrations → Slack DMs). Invite
                  Threadbot to the channel, then hit refresh. Silently skips when no channel
                  is configured.
                </p>
              </div>

              <div className="space-y-4 pt-2 border-t">
                <p className="text-sm font-medium">Which events to post</p>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Beaker className="h-4 w-4 text-threadheads-purple shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-pd-samples">Samples</Label>
                      <p className="text-sm text-muted-foreground">
                        Sample requested, received, or approved.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-pd-samples"
                    checked={notifSettings.notifyOnProductDevSamples}
                    onCheckedChange={(checked) => updateNotif({ notifyOnProductDevSamples: checked })}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <GitBranch className="h-4 w-4 text-threadheads-purple shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-pd-stages">Stage and status changes</Label>
                      <p className="text-sm text-muted-foreground">
                        When a record moves stage or changes status.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-pd-stages"
                    checked={notifSettings.notifyOnProductDevStages}
                    onCheckedChange={(checked) => updateNotif({ notifyOnProductDevStages: checked })}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-4 w-4 text-threadheads-purple shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-pd-comments">Comments</Label>
                      <p className="text-sm text-muted-foreground">
                        When a new comment is posted on a record.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-pd-comments"
                    checked={notifSettings.notifyOnProductDevComments}
                    onCheckedChange={(checked) => updateNotif({ notifyOnProductDevComments: checked })}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Paperclip className="h-4 w-4 text-threadheads-purple shrink-0" />
                    <div className="space-y-0.5">
                      <Label htmlFor="notify-pd-files">File uploads</Label>
                      <p className="text-sm text-muted-foreground">
                        When a new file is attached to a record.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="notify-pd-files"
                    checked={notifSettings.notifyOnProductDevFiles}
                    onCheckedChange={(checked) => updateNotif({ notifyOnProductDevFiles: checked })}
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Partner inventory digests — self-contained (own mutations), not part
            of the org-settings form save above. */}
        <PartnerDigestsCard />

        {/* Schedule note */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Send schedule
            </CardTitle>
            <CardDescription>
              PO and out-of-stock digests are sent daily at <strong>22:00 UTC</strong>
              {' '}(08:00 AEST in winter, 09:00 AEDT in summer). The crons are idempotent, a
              Railway restart won't double-fire. Stock-sync events fire immediately when
              triggered, regardless of schedule.
            </CardDescription>
          </CardHeader>
        </Card>

        {hasChanges && (
          <div className="sticky bottom-4 z-10 flex justify-center">
            <div className="inline-flex items-center gap-4 rounded-lg bg-primary px-5 py-3 text-primary-foreground shadow-lg">
              <p className="text-sm text-primary-foreground/80">Unsaved changes</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleReset}
                  disabled={updateOrganization.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="secondary"
                  size="sm"
                  disabled={updateOrganization.isPending}
                >
                  {updateOrganization.isPending ? (
                    <>
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-1.5 h-3.5 w-3.5" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Sibling of the form, not inside it: this card writes its own store
          (prod_settings.notification_routing) with its own save, and nesting a
          form inside a form is invalid HTML. See SplitOrderNotificationCard. */}
      <SplitOrderNotificationCard />
    </div>
  );
}
